import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
    IconButton,
    Card,
    Stack,
    Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { auth, db } from '../../firebase';
import {
    collection,
    addDoc,
    Timestamp,
    query,
    orderBy,
    onSnapshot,
} from 'firebase/firestore';
import Shopheader from '../Shop/Shopheader';
import Mashalheader from '../Mashal/Mashalheader';
import Wholesalerheader from '../Wholesale/Wholesaleheader';
import { useParams, useNavigate } from 'react-router-dom';

export default function Purchase() {
    const { category } = useParams();
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);
    const [dealers, setDealers] = useState([]);
    const [products, setProducts] = useState([]);
    const [purchaseItems, setPurchaseItems] = useState([]);

    const [dealerId, setDealerId] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [paytype, setPaytype] = useState('Cash');

    const [itemId, setItemId] = useState('');
    const [qty, setQty] = useState(1);
    const [rate, setRate] = useState('');
    const [discount, setDiscount] = useState(0);

    const [subtotal, setSubtotal] = useState('');
    const [totalAmount, setTotalAmount] = useState('');
    const [grandTotal, setGrandTotal] = useState(0);
    const [paidAmount, setPaidAmount] = useState(0);
    const [remaining, setRemaining] = useState(0);

    useEffect(() => {
        auth.onAuthStateChanged((user) => {
            setUserData(user);
        });
    }, []);

    useEffect(() => {
        fetchProductsAndVendors();
        const today = new Date();
        setPurchaseDate(today.toISOString().split('T')[0]);
    }, [category]);

    const fetchProductsAndVendors = async () => {
        try {
            const qVendors = query(collection(db, 'vendors'));
            onSnapshot(qVendors, (snap) => {
                setDealers(snap.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
            });

            const qProducts = query(
                collection(db, 'products'),
                orderBy('productname', 'asc')
            );
            onSnapshot(qProducts, (snap) => {
                setProducts(snap.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
            });
        } catch (error) {
            toast.error(error.message);
        }
    };

    // ---------------- CALCULATIONS ----------------
    const calculateItemTotal = (r, q, d) => {
        const subtotal = parseFloat(r || 0) * parseFloat(q || 0) - parseFloat(d || 0);
        return parseFloat(subtotal.toFixed(2));
    };

    const updateGrandTotal = (items) => {
        const total = items.reduce((acc, item) => acc + parseFloat(item.totalAmount || 0), 0);
        setGrandTotal(total.toFixed(2));
        setRemaining((total - parseFloat(paidAmount || 0)).toFixed(2));
    };

    // ---------------- HANDLE PRODUCT SELECT ----------------
    const handleProductSelect = (prod) => {
        if (!prod) {
            setItemId('');
            setRate('');
            setSubtotal('');
            setTotalAmount('');
            return;
        }
        setItemId(prod.id);
        const r = parseFloat(prod.data.salerate || 0);
        setRate(r);
        const subtotal = calculateItemTotal(r, qty, discount);
        setSubtotal(subtotal);
        setTotalAmount(subtotal);
    };

    const handleQtyOrDiscountChange = (newQty, newDiscount, newRate) => {
        const subtotal = calculateItemTotal(newRate, newQty, newDiscount);
        setSubtotal(subtotal);
        setTotalAmount(subtotal);
    };

    const addPurchaseItems = () => {
        if (!itemId || !rate || !qty) {
            toast.error('Please fill required fields!');
            return;
        }

        const newItem = {
            itemId,
            qty: parseFloat(qty),
            rate: parseFloat(rate),
            discount: parseFloat(discount),
            totalAmount: parseFloat(totalAmount),
        };

        const updatedItems = [...purchaseItems, newItem];
        setPurchaseItems(updatedItems);

        // Update grand total
        updateGrandTotal(updatedItems);

        // Reset item input
        setItemId('');
        setQty(1);
        setRate('');
        setDiscount(0);
        setSubtotal('');
        setTotalAmount('');

        toast.success('Item added locally');
    };

    const savePurchase = async () => {
        if (!dealerId || !purchaseDate || purchaseItems.length === 0) {
            toast.error('Please select vendor, date and add items!');
            return;
        }

        try {
            // 1️⃣ Save purchase
            const purchaseRef = await addDoc(collection(db, 'purchases'), {
                dealerId,
                purchaseDate,
                grandTotal: parseFloat(grandTotal),
                paidAmount: parseFloat(paidAmount),
                remaining: parseFloat(remaining),
                paytype,
                category,
                status: 'Pending',
                uid: userData?.uid,
                created: Timestamp.now(),
            });

            const purchaseId = purchaseRef.id;

            // 2️⃣ Save purchase items
            const batchPromises = purchaseItems.map((item) =>
                addDoc(collection(db, 'purchasesitems'), {
                    purchaseid: purchaseId,
                    dealerId,
                    purchaseDate,
                    ...item,
                    category,
                    status: 'Pending',
                    uid: userData?.uid,
                    created: Timestamp.now(),
                })
            );

            // 3️⃣ Save payment history separately
            if (parseFloat(paidAmount) > 0) {
                await addDoc(collection(db, 'vendorpaymenthistory'), {
                    dealerId,
                    purchaseId,
                    paymentDate: purchaseDate,
                    amount: parseFloat(paidAmount),
                    paytype,
                    uid: userData?.uid,
                    created: Timestamp.now(),
                });
            }

            await Promise.all(batchPromises);

            toast.success('Purchase saved successfully');

            // Clear local state
            setPurchaseItems([]);
            setDealerId('');
            setGrandTotal(0);
            setPaidAmount(0);
            setRemaining(0);

        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <Box p={2}>
            {category === "shop" && <Shopheader />}
            {category === "mashal" && <Mashalheader />}
            {category === "wholesaler" && <Wholesalerheader />}

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Button
                    variant="contained"
                    onClick={() => navigate(`/view-purchase/${category}`)}
                >
                    खरेदी बघा 
                </Button>
                <Typography variant="h5" fontWeight="bold">खरेदी</Typography>
            </Stack>

            <Card sx={{ p: 2 }}>
                <Grid container className="row" spacing={2}>
                    <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>विक्रेता </InputLabel>
                            <Select value={dealerId} onChange={(e) => setDealerId(e.target.value)}>
                                {dealers.map(d => (
                                    <MenuItem key={d.id} value={d.id}>{d.data.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="खरेदी दिनांक "
                            InputLabelProps={{ shrink: true }}
                            value={purchaseDate}
                            onChange={(e) => setPurchaseDate(e.target.value)}
                            size="small"
                        />
                    </Grid>
                </Grid>

                {/* ---------------- ITEM SECTION ---------------- */}
                <Box mt={2}>
                    {/* Input Row */}
                    <div className="row mb-3">
                        <div className="col-sm-3 mt-2">
                            <Autocomplete
                                size="small"
                                options={products}
                                getOptionLabel={(option) => option.data.productname || ""}
                                value={products.find(p => p.id === itemId) || null}
                                onChange={(e, newValue) => handleProductSelect(newValue)}
                                renderInput={(params) => <TextField {...params} label="प्रॉडक्ट" />}
                            />
                        </div>

                        <div className="col-sm-2 mt-2">
                            <TextField
                                label="रेट"
                                value={rate}
                                size="small"
                                fullWidth
                                onChange={(e) => {
                                    const newRate = e.target.value;
                                    setRate(newRate);
                                    handleQtyOrDiscountChange(qty, discount, newRate);
                                }}
                            />
                        </div>

                        <div className="col-sm-2 mt-2">
                            <TextField
                                label="नग"
                                value={qty}
                                size="small"
                                fullWidth
                                onChange={(e) => {
                                    const newQty = e.target.value;
                                    setQty(newQty);
                                    handleQtyOrDiscountChange(newQty, discount, rate);
                                }}
                            />
                        </div>

                        <div className="col-sm-2 mt-2">
                            <TextField
                                label="डिस्काउंट"
                                value={discount}
                                size="small"
                                fullWidth
                                onChange={(e) => {
                                    const newDiscount = e.target.value;
                                    setDiscount(newDiscount);
                                    handleQtyOrDiscountChange(qty, newDiscount, rate);
                                }}
                            />
                        </div>

                        <div className="col-sm-2 mt-2">
                            <TextField
                                label="टोटल"
                                value={totalAmount}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </div>

                        <div className="col-sm-1 d-flex align-items-center mt-2">
                            <IconButton color="primary" onClick={addPurchaseItems}>
                                <AddIcon />
                            </IconButton>
                        </div>
                    </div>

                    {/* Purchase Items List */}
                    {purchaseItems.map((item, i) => (
                        <div className="row border-bottom py-2" key={i}>
                            <div className="col-sm-3 mt-2">
                                {products.find((p) => p.id === item.itemId)?.data.productname || item.itemId}
                            </div>
                            <div className="col-sm-2 mt-2">{item.rate}</div>
                            <div className="col-sm-2 mt-2">{item.qty}</div>
                            <div className="col-sm-2 mt-2">{item.discount}</div>
                            <div className="col-sm-2 mt-2">{item.totalAmount}</div>
                            <div className="col-sm-1 mt-2">
                                <IconButton color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                </Box>

                {/* ---------------- GRAND TOTALS ---------------- */}
                <Box mt={3}>
                    <Grid className="row" >
                        <Grid className="col-sm-3 mb-3" >
                            <TextField label="ग्रँड टोटल" fullWidth value={grandTotal} InputProps={{ readOnly: true }} size="small" />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" >
                            <TextField
                                label="जमा रक्कम"
                                fullWidth
                                value={paidAmount}
                                onChange={(e) => {
                                    setPaidAmount(e.target.value);
                                    setRemaining((grandTotal - e.target.value).toFixed(2));
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" >
                            <FormControl fullWidth size="small">
                                <InputLabel>पेमेंट टाईप </InputLabel>
                                <Select value={paytype} onChange={(e) => setPaytype(e.target.value)}>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Online Pay">Online Pay</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid className="col-sm-3 mb-3" >
                            <TextField label="बाकी" fullWidth value={remaining} InputProps={{ readOnly: true }} size="small" />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={2} display="flex" justifyContent="center">
                    <Button variant="contained" color="primary" onClick={savePurchase}>
                        Save Purchase
                    </Button>
                </Box>
            </Card>
        </Box>
    );
}
